import i18next from "i18next";
import createCachedSelector from "re-reselect";
import { ApplicationStore } from "src/store";
import { getVehicleType } from "src/store/selectors/vehicles";

/**********************************************************************************************************************
 * Credit Rating Settings Selectors
 *********************************************************************************************************************/

export const getCreditScoreDefault = (state: ApplicationStore): number =>
    state.account.integration_settings?.Financing?.default_credit_score ?? 680;

/***=================================================================================
 * Calculate Taxes Settings Selectors
 *==================================================================================*/

export const getCalculateTaxesType = (state: ApplicationStore) =>
    state.account?.integration_settings?.Financing?.calculate_taxes;

export const isCalculateTaxesEnabled = (state: ApplicationStore): boolean =>
    state.account?.integration_settings?.Financing?.calculate_taxes === "enabled";

export const isCalculateTaxesCustomerRequest = (state: ApplicationStore): boolean =>
    state.account?.integration_settings?.Financing?.calculate_taxes === "customer_requested";

export const isCalculateTaxesDisabled = (state: ApplicationStore): boolean =>
    state.account?.integration_settings?.Financing?.calculate_taxes === "disabled";

/**
 * Payment Frequency Selectors
 */

// TODO: ONSHOP-6623 We don't currently expand integration_settings.Financing on vehicle.account, which would be more correct
export const getEnabledPaymentFrequencyOptions = createCachedSelector(
    (state: ApplicationStore) => state.account?.integration_settings?.Financing.payment_frequencies,
    (state: ApplicationStore, vin: string) => getVehicleType(state, vin),
    (state: ApplicationStore, vin: string, paymentType: string) => paymentType,
    (paymentFrequencies, vehicleType, paymentType) => {
        if (!paymentFrequencies || !vehicleType) {
            return { monthly: true, biweekly: false, weekly: false };
        }

        const vehiclePaymentConfig = paymentFrequencies[vehicleType]?.[paymentType];

        return {
            // Exclude default
            monthly: vehiclePaymentConfig?.monthly,
            biweekly: vehiclePaymentConfig?.biweekly,
            weekly: vehiclePaymentConfig?.weekly,
        };
    }
)((state: ApplicationStore, vin: string, paymentType: string) => `${vin}:${paymentType}`);

export const frequencyTranslationLookup = (value: string) =>
    ({
        weekly: i18next.t("CustomizePayment.PaymentFrequency.weekly"),
        biweekly: i18next.t("CustomizePayment.PaymentFrequency.biweekly"),
        monthly: i18next.t("CustomizePayment.PaymentFrequency.monthly"),
    }[value]);

export const getPaymentFrequencyOptionsArray = createCachedSelector(getEnabledPaymentFrequencyOptions, (options) => {
    return Object.entries(options)
        .filter(([frequencyKey, enabled]) => enabled)
        .map(([frequencyKey, enabled]) => ({
            value: frequencyKey,
            text: frequencyTranslationLookup(frequencyKey),
        }));
})((state: ApplicationStore, vin: string, paymentType: string) => `${vin}:${paymentType}`);

/*=============================================
Feature flags and UI settings
 =============================================*/

export const showLeaseTotalCapCost = (state: ApplicationStore): boolean => {
    return !state.account?.integration_settings?.Financing?.hide_field?.lease_total_cap_cost ?? true;
};
